import { Card as CardAD } from 'antd';
import cs from 'classnames';
import type { ComponentProps, ReactNode } from 'react';
import { Title, type TitleProperties } from '../typography';
import styles from './card.module.css';
import { FullWidth, type ModalFullWidthProperties } from './full-width';
import { EntitySkeleton, HorizontalScroll, type HorizontalScrollProperties } from './horizontal-scroll';

export type CardProperties = {
  fullHeight?: boolean;
  marginBottom?: boolean;
  shrinked?: boolean;
  shrinkedBottom?: boolean;
  shrinkedLeft?: boolean;
  shrinkedRight?: boolean;
  shrinkedTop?: boolean;
  title?: ReactNode;
} & ComponentProps<typeof CardAD>;

const Card = ({
  className,
  classNames,
  fullHeight,
  marginBottom = true,
  shrinked,
  shrinkedBottom,
  shrinkedLeft,
  shrinkedRight,
  shrinkedTop,
  title,
  ...rest
}: CardProperties) => (
  <CardAD
    className={cs(styles.card, className, {
      [styles['card--full-height'] ?? '']: fullHeight,
      [styles['card--margin-bottom'] ?? '']: marginBottom,
      [styles['card--shrinked-bottom'] ?? '']: shrinkedBottom,
      [styles['card--shrinked-left'] ?? '']: shrinkedLeft,
      [styles['card--shrinked-right'] ?? '']: shrinkedRight,
      [styles['card--shrinked-top'] ?? '']: shrinkedTop,
      [styles['card--shrinked'] ?? '']: shrinked,
    })}
    classNames={
      typeof classNames === 'function'
        ? (info) => {
            const resolvedClassNames = classNames(info);

            return {
              ...resolvedClassNames,
              body: cs(styles.body, resolvedClassNames?.body),
              extra: cs(styles.extra, resolvedClassNames?.extra),
              header: cs(styles.header, resolvedClassNames?.header),
              root: cs(styles.root, resolvedClassNames?.root),
            };
          }
        : {
            ...classNames,
            body: cs(styles.body, classNames?.body),
            extra: cs(styles.extra, classNames?.extra),
            header: cs(styles.header, classNames?.header),
            root: cs(styles.root, classNames?.root),
          }
    }
    title={typeof title === 'string' ? <Title level={4}>{title}</Title> : title}
    variant="borderless"
    {...rest}
  />
);

Card.Title = function ({ level = 4, ...rest }: TitleProperties) {
  return <Title level={level} {...rest} />;
};

Card.FullWidth = function ({ className, ...rest }: ModalFullWidthProperties) {
  return <FullWidth className={cs(className, styles['card-full-width'])} {...rest} />;
};

Card.HorizontalScroll = Object.assign(
  function ({ className, ...rest }: HorizontalScrollProperties) {
    return <HorizontalScroll className={cs(className, styles['card-horizontal-scroll'])} {...rest} />;
  },
  {
    EntitySkeleton,
  },
);

export default Card;
